import { recurrenceUntilCalendarDate } from '../utils/recurrenceDates';
import { parseCalendarDate, toUtcMidnight } from '@shared/dates/calendarDate';
import { useState } from 'react';
import { RRule, rrulestr, Options as RRuleOptions, Weekday, WeekdayStr } from 'rrule';
import type { CustomRecurrenceRule } from '../components/CustomRecurrenceModal';
import { RECURRENCE_OPTIONS } from './useDurationSchedule';

export interface RecurrenceRuleResult {
  rrule: string;
  endDate: string | null;
}

// Map our internal day of week values to RRule constants
const DAY_MAP: Record<WeekdayStr, Weekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};

// Map frequency values to RRule constants
const FREQ_MAP: Record<string, number> = {
  daily: RRule.DAILY,
  weekly: RRule.WEEKLY,
  monthly: RRule.MONTHLY,
  yearly: RRule.YEARLY,
};

export function useRecurrence(initialRule?: string) {
  const [recurrence, setRecurrence] = useState('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [hasRecurrenceEnd, setHasRecurrenceEnd] = useState(false);
  const [recurrenceCount, setRecurrenceCount] = useState<string | null>(null);
  const [isCustomRecurrenceModalOpen, setIsCustomRecurrenceModalOpen] = useState(false);
  const [customRecurrenceRule, setCustomRecurrenceRule] = useState<CustomRecurrenceRule | null>(
    null,
  );

  // Handle recurrence selection change
  const handleRecurrenceChange = (value: string) => {
    if (value === 'custom') {
      setIsCustomRecurrenceModalOpen(true);
    } else {
      setRecurrence(value);
    }
  };

  // Handle custom recurrence save
  const handleCustomRecurrenceSave = (rule: CustomRecurrenceRule) => {
    setCustomRecurrenceRule(rule);
    setRecurrence('custom');
  };

  // Helper function to extract weekdays from RRule
  const extractWeekdaysFromRRule = (rule: RRule): WeekdayStr[] => {
    const byweekday = rule.origOptions.byweekday;
    if (!byweekday) return ['MO']; // Default

    // Handle different formats of byweekday based on what the library provides
    if (Array.isArray(byweekday)) {
      // Attempt to extract weekday values
      return byweekday.map(day => {
        // Handle Weekday objects which have a specific format
        if (typeof day === 'object' && day !== null) {
          if (day.toString().includes('MO')) return 'MO';
          if (day.toString().includes('TU')) return 'TU';
          if (day.toString().includes('WE')) return 'WE';
          if (day.toString().includes('TH')) return 'TH';
          if (day.toString().includes('FR')) return 'FR';
          if (day.toString().includes('SA')) return 'SA';
          if (day.toString().includes('SU')) return 'SU';
        }
        // Handle direct string values (shouldn't normally happen but just in case)
        if (typeof day === 'string' && Object.keys(DAY_MAP).includes(day)) {
          return day as WeekdayStr;
        }
        return 'MO'; // Default fallback
      });
    }

    // Handle single Weekday object
    if (typeof byweekday === 'object' && byweekday !== null) {
      if (byweekday.toString().includes('MO')) return ['MO'];
      if (byweekday.toString().includes('TU')) return ['TU'];
      if (byweekday.toString().includes('WE')) return ['WE'];
      if (byweekday.toString().includes('TH')) return ['TH'];
      if (byweekday.toString().includes('FR')) return ['FR'];
      if (byweekday.toString().includes('SA')) return ['SA'];
      if (byweekday.toString().includes('SU')) return ['SU'];
    }

    return ['MO']; // Default
  };

  // Parse existing rrule string (useful for editing existing appointments)
  const parseRRule = (rruleString: string) => {
    try {
      // Parse the RRule string using the rrule library
      const rule = rrulestr(rruleString);

      // Use RRule type assertions properly
      const ruleAsRRule = rule instanceof RRule ? rule : null;

      if (!ruleAsRRule) {
        console.error('Parsed result is not an RRule instance');
        setRecurrence('none');
        return;
      }

      // Check if it's a custom rule
      const isCustomRule =
        ruleAsRRule.origOptions.byweekday !== undefined ||
        (ruleAsRRule.origOptions.interval &&
          ruleAsRRule.origOptions.interval > 1 &&
          ruleAsRRule.origOptions.freq !== RRule.WEEKLY) ||
        (ruleAsRRule.origOptions.interval &&
          ruleAsRRule.origOptions.interval !== 2 &&
          ruleAsRRule.origOptions.freq === RRule.WEEKLY) ||
        ruleAsRRule.origOptions.bymonthday !== undefined ||
        ruleAsRRule.origOptions.bysetpos !== undefined;

      if (isCustomRule) {
        setRecurrence('custom');

        // Convert RRule frequency to our format
        let frequency = 'weekly';
        switch (ruleAsRRule.options.freq) {
          case RRule.DAILY:
            frequency = 'daily';
            break;
          case RRule.WEEKLY:
            frequency = 'weekly';
            break;
          case RRule.MONTHLY:
            frequency = 'monthly';
            break;
          case RRule.YEARLY:
            frequency = 'yearly';
            break;
        }

        // Extract interval
        const interval = ruleAsRRule.options.interval || 1;

        // Extract weekdays using our helper function
        const weekDays = extractWeekdaysFromRRule(ruleAsRRule);

        // Extract end mode and details
        let endMode: 'never' | 'on' | 'after' = 'never';
        let endDate: string | null = null;
        let endCount: number | null = null;

        if (ruleAsRRule.options.count) {
          endMode = 'after';
          endCount = ruleAsRRule.options.count;
          setRecurrenceCount(endCount.toString());
        } else if (ruleAsRRule.options.until) {
          endMode = 'on';
          const untilDate = ruleAsRRule.options.until;
          endDate = recurrenceUntilCalendarDate(rruleString, untilDate);
          setRecurrenceEndDate(endDate);
          setHasRecurrenceEnd(true);
        }

        // Handle monthly patterns
        let monthlyPattern: 'dayOfMonth' | 'dayOfWeek' = 'dayOfMonth';
        let monthlyDay = new Date().getDate();
        let monthlyDayPosition: 1 | 2 | 3 | 4 | -1 = 1;

        if (frequency === 'monthly') {
          const bymonthday = ruleAsRRule.origOptions.bymonthday;
          const bysetpos = ruleAsRRule.origOptions.bysetpos;

          if (bymonthday !== undefined) {
            monthlyPattern = 'dayOfMonth';
            monthlyDay = Array.isArray(bymonthday) ? bymonthday[0] : (bymonthday as number);
          } else if (bysetpos !== undefined && ruleAsRRule.origOptions.byweekday !== undefined) {
            monthlyPattern = 'dayOfWeek';
            monthlyDayPosition = Array.isArray(bysetpos)
              ? (bysetpos[0] as 1 | 2 | 3 | 4 | -1)
              : (bysetpos as 1 | 2 | 3 | 4 | -1);
          }
        }

        // Set custom recurrence rule
        const customRule: CustomRecurrenceRule = {
          frequency,
          interval,
          weekDays,
          endMode,
          endDate,
          endCount,
          monthlyPattern,
          monthlyDay,
          monthlyDayPosition,
        };

        setCustomRecurrenceRule(customRule);
      } else {
        // Standard recurrence options
        if (ruleAsRRule.options.freq === RRule.DAILY) {
          setRecurrence('daily');
        } else if (ruleAsRRule.options.freq === RRule.WEEKLY) {
          // Check if it's biweekly or weekly
          if (ruleAsRRule.options.interval === 2) {
            setRecurrence('biweekly');
          } else {
            setRecurrence('weekly');
          }
        } else if (ruleAsRRule.options.freq === RRule.MONTHLY) {
          setRecurrence('monthly');
        } else if (ruleAsRRule.options.freq === RRule.YEARLY) {
          setRecurrence('yearly');
        } else {
          setRecurrence('none');
        }

        // Extract count or until date
        if (ruleAsRRule.options.count) {
          setRecurrenceCount(ruleAsRRule.options.count.toString());
          setHasRecurrenceEnd(false);
        } else if (ruleAsRRule.options.until) {
          const untilDate = ruleAsRRule.options.until;
          const formattedDate = recurrenceUntilCalendarDate(rruleString, untilDate);
          setRecurrenceEndDate(formattedDate);
          setHasRecurrenceEnd(true);
        }
      }
    } catch (error) {
      console.error('Error parsing RRule:', error);
      setRecurrence('none');
    }
  };

  // Generate RRULE from current recurrence settings
  const generateRRule = (): RecurrenceRuleResult | null => {
    if (recurrence === 'none') return null;

    try {
      const rruleOptions: Partial<RRuleOptions> = {};

      if (recurrence === 'custom' && customRecurrenceRule) {
        // Set frequency
        rruleOptions.freq = FREQ_MAP[customRecurrenceRule.frequency];

        // Set interval if > 1
        if (customRecurrenceRule.interval > 1) {
          rruleOptions.interval = customRecurrenceRule.interval;
        }

        // Add weekdays for weekly recurrence
        if (
          customRecurrenceRule.frequency === 'weekly' &&
          customRecurrenceRule.weekDays.length > 0
        ) {
          // Use the proper weekday constants
          rruleOptions.byweekday = customRecurrenceRule.weekDays.map(
            day => DAY_MAP[day as WeekdayStr],
          );
        }

        // Handle monthly recurrence patterns
        if (customRecurrenceRule.frequency === 'monthly') {
          if (
            customRecurrenceRule.monthlyPattern === 'dayOfMonth' &&
            customRecurrenceRule.monthlyDay
          ) {
            // Pattern like "15th of every month"
            rruleOptions.bymonthday = customRecurrenceRule.monthlyDay;
          } else if (
            customRecurrenceRule.monthlyPattern === 'dayOfWeek' &&
            customRecurrenceRule.weekDays &&
            customRecurrenceRule.weekDays.length > 0 &&
            customRecurrenceRule.monthlyDayPosition
          ) {
            // Pattern like "First Monday of every month"
            // Here we use the nth method on Weekday
            const dayOfWeek = DAY_MAP[customRecurrenceRule.weekDays[0] as WeekdayStr];
            rruleOptions.byweekday = [dayOfWeek.nth(customRecurrenceRule.monthlyDayPosition)];
          }
        }

        // Add end conditions
        if (customRecurrenceRule.endMode === 'on' && customRecurrenceRule.endDate) {
          const calendarEnd = parseCalendarDate(customRecurrenceRule.endDate);
          if (!calendarEnd) return null;
          const endDate = toUtcMidnight(calendarEnd);
          // Clear time portion to ensure midnight UTC
          endDate.setUTCHours(0, 0, 0, 0);
          rruleOptions.until = endDate;

          return {
            rrule: new RRule(rruleOptions).toString(),
            endDate: endDate.toISOString(),
          };
        } else if (customRecurrenceRule.endMode === 'after' && customRecurrenceRule.endCount) {
          rruleOptions.count = customRecurrenceRule.endCount;

          return {
            rrule: new RRule(rruleOptions).toString(),
            endDate: null,
          };
        }

        // No end date specified
        return {
          rrule: new RRule(rruleOptions).toString(),
          endDate: null,
        };
      } else {
        // Standard recurrence options
        switch (recurrence) {
          case 'daily':
            rruleOptions.freq = RRule.DAILY;
            break;
          case 'weekly':
            rruleOptions.freq = RRule.WEEKLY;
            break;
          case 'biweekly':
            rruleOptions.freq = RRule.WEEKLY;
            rruleOptions.interval = 2;
            break;
          case 'monthly':
            rruleOptions.freq = RRule.MONTHLY;
            break;
          case 'yearly':
            rruleOptions.freq = RRule.YEARLY;
            break;
          default:
            return null;
        }

        if (hasRecurrenceEnd && recurrenceEndDate) {
          const calendarEnd = parseCalendarDate(recurrenceEndDate);
          if (!calendarEnd) return null;
          const endDate = toUtcMidnight(calendarEnd);
          // Clear time portion to ensure midnight UTC
          endDate.setUTCHours(0, 0, 0, 0);
          rruleOptions.until = endDate;

          return {
            rrule: new RRule(rruleOptions).toString(),
            endDate: endDate.toISOString(),
          };
        } else if (recurrenceCount) {
          const count = parseInt(recurrenceCount, 10);
          if (!isNaN(count) && count > 0) {
            rruleOptions.count = count;
          }

          return {
            rrule: new RRule(rruleOptions).toString(),
            endDate: null,
          };
        }

        return {
          rrule: new RRule(rruleOptions).toString(),
          endDate: null,
        };
      }
    } catch (error) {
      console.error('Error generating RRule:', error);
      return null;
    }
  };

  // Helper to get a human-readable representation of the current recurrence rule
  const getRecurrenceText = (): string => {
    try {
      if (recurrence === 'none') return 'Does not repeat';

      if (recurrence === 'custom' && customRecurrenceRule) {
        // Create a temporary RRule to use the .toText() method
        const options: Partial<RRuleOptions> = {
          freq: FREQ_MAP[customRecurrenceRule.frequency],
          interval: customRecurrenceRule.interval,
        };

        if (
          customRecurrenceRule.frequency === 'weekly' &&
          customRecurrenceRule.weekDays.length > 0
        ) {
          options.byweekday = customRecurrenceRule.weekDays.map(day => DAY_MAP[day as WeekdayStr]);
        }

        // Handle monthly patterns with proper methods from the library
        if (customRecurrenceRule.frequency === 'monthly') {
          if (
            customRecurrenceRule.monthlyPattern === 'dayOfMonth' &&
            customRecurrenceRule.monthlyDay
          ) {
            options.bymonthday = customRecurrenceRule.monthlyDay;
          } else if (
            customRecurrenceRule.monthlyPattern === 'dayOfWeek' &&
            customRecurrenceRule.weekDays?.length > 0 &&
            customRecurrenceRule.monthlyDayPosition
          ) {
            const dayOfWeek = DAY_MAP[customRecurrenceRule.weekDays[0] as WeekdayStr];
            options.byweekday = [dayOfWeek.nth(customRecurrenceRule.monthlyDayPosition)];
          }
        }

        if (customRecurrenceRule.endMode === 'on' && customRecurrenceRule.endDate) {
          const calendarEnd = parseCalendarDate(customRecurrenceRule.endDate);
          if (!calendarEnd) return 'Custom recurrence';
          options.until = toUtcMidnight(calendarEnd);
        } else if (customRecurrenceRule.endMode === 'after' && customRecurrenceRule.endCount) {
          options.count = customRecurrenceRule.endCount;
        }

        // Create a temporary rule for text representation
        const tempRule = new RRule(options);
        return tempRule.toText();
      }

      // For standard options, use a preset text
      const option = RECURRENCE_OPTIONS.find(opt => opt.value === recurrence);
      // Return capitalized label
      return option ? option.label : 'Custom recurrence';
    } catch (error) {
      console.error('Error generating recurrence text:', error);
      return 'Custom recurrence';
    }
  };

  return {
    recurrence,
    recurrenceEndDate,
    hasRecurrenceEnd,
    recurrenceCount,
    isCustomRecurrenceModalOpen,
    customRecurrenceRule,
    setRecurrence,
    setRecurrenceEndDate,
    setHasRecurrenceEnd,
    setRecurrenceCount,
    setIsCustomRecurrenceModalOpen,
    setCustomRecurrenceRule,
    handleRecurrenceChange,
    handleCustomRecurrenceSave,
    generateRRule,
    parseRRule,
    getRecurrenceText,
  };
}

export type UseRecurrenceReturn = ReturnType<typeof useRecurrence>;
